import { OrderQuotePurpose, useUpdateOrderQuoteMutation } from '@codezo/graphql';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import { DatePicker } from '../../../../components/ui/date-picker';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../../../components/ui/dialog';
import { Field } from '../../../../components/ui/field';
import { Input } from '../../../../components/ui/input';
import { SelectPopover } from '../../../../components/ui/select-popover';
import { Textarea } from '../../../../components/ui/textarea';

const PURPOSE_OPTIONS = [
	{ value: OrderQuotePurpose.NotSpecified, label: 'Not specified' },
	{ value: OrderQuotePurpose.Packaging, label: 'Packaging' },
	{ value: OrderQuotePurpose.Event, label: 'Event' },
	{ value: OrderQuotePurpose.Resell, label: 'Resell' },
	{ value: OrderQuotePurpose.Other, label: 'Other' },
];

interface EditOrderQuoteDetailsValue {
	name: string;
	description?: string | null;
	purpose?: string | null;
	purposeOther?: string | null;
	deadline?: string | null;
}

interface EditOrderQuoteDetailsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	quoteId: string;
	initialValues: EditOrderQuoteDetailsValue;
	onSuccess: () => void;
}

/**
 * Quote-level details the client owns: name, purpose, deadline, description.
 * The org rail groups all of these into a single "Details" card, so unlike
 * admin (which splits details/description across two modals) one modal covers
 * the whole card.
 */
export function EditOrderQuoteDetailsModal({
	open,
	onOpenChange,
	quoteId,
	initialValues,
	onSuccess,
}: EditOrderQuoteDetailsModalProps) {
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [purpose, setPurpose] = useState('');
	const [purposeOther, setPurposeOther] = useState('');
	const [deadline, setDeadline] = useState<string | null>(null);

	const [updateQuote, { loading }] = useUpdateOrderQuoteMutation();

	// Callers pass initialValues as an object literal, so it's a new reference on
	// every render. Read it through a ref and key the reset off `open` alone —
	// otherwise an unrelated parent re-render would wipe what's being typed.
	const initialRef = useRef(initialValues);
	initialRef.current = initialValues;

	useEffect(() => {
		if (!open) return;
		const v = initialRef.current;
		setName(v.name);
		setDescription(v.description ?? '');
		setPurpose(v.purpose ?? '');
		setPurposeOther(v.purposeOther ?? '');
		setDeadline(v.deadline ?? null);
	}, [open]);

	const isOther = purpose === OrderQuotePurpose.Other;

	const hasChanges =
		name.trim() !== initialValues.name ||
		description.trim() !== (initialValues.description ?? '') ||
		purpose !== (initialValues.purpose ?? '') ||
		(isOther ? purposeOther.trim() : '') !== (initialValues.purposeOther ?? '') ||
		deadline !== (initialValues.deadline ?? null);

	const handleSubmit = async () => {
		if (!name.trim()) return;
		try {
			const { data } = await updateQuote({
				variables: {
					id: quoteId,
					input: {
						name: name.trim(),
						description: description.trim(),
						purpose: purpose ? (purpose as OrderQuotePurpose) : undefined,
						// Cleared when the purpose is no longer "Other", so a stale
						// detail can't outlive the purpose it belonged to.
						purposeOther: isOther ? purposeOther.trim() : '',
						// null (not undefined) so clearing the deadline persists.
						deadline: deadline,
					},
				},
			});

			// The API reports permission/validation failures as success: false
			// rather than throwing, so a thrown error isn't the only failure mode.
			if (data?.updateOrderQuote?.success) {
				toast.success('Quote updated');
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(data?.updateOrderQuote?.message || 'Failed to update quote');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update quote');
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit details</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<Field label="Name" required>
								<Input
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Spring Collection 2026"
								/>
							</Field>

							<Field label="Purpose">
								<SelectPopover
									value={purpose}
									onChange={setPurpose}
									options={PURPOSE_OPTIONS}
									placeholder="Select purpose"
								/>
							</Field>

							{isOther && (
								<Field label="Purpose details">
									<Input
										value={purposeOther}
										onChange={(e) => setPurposeOther(e.target.value)}
										placeholder="Describe the purpose"
									/>
								</Field>
							)}

							<Field label="Deadline">
								<DatePicker
									value={deadline ? new Date(deadline) : null}
									onChange={(d: Date | undefined) =>
										setDeadline(d?.toISOString() ?? null)
									}
									minDate={new Date()}
									placeholder="No deadline"
									clearable
								/>
							</Field>

							<Field label="Description">
								<Textarea
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="Brief description of this quote"
									rows={3}
								/>
							</Field>
						</div>
					</DialogBody>
					<DialogFooter>
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || !hasChanges || loading}>
							{loading ? 'Saving…' : 'Save'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
